import React, { useMemo } from 'react';
import {
  ComputedExerciseProgress,
  ExerciseChartDataPoint,
  ExerciseChartScale,
  ExerciseProgressChart,
  FinalChartDataPoint,
} from '../../types/Fisiofit/exerciseHistory';
import { ChartOptions } from 'chart.js';
import { ExerciseProgressMetric } from './ExerciseProgressMetric';
import { Chart } from 'primereact/chart';
import { EXERCISE_TYPE } from '../../types/Fisiofit/exercise';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import { getColors } from '../../utils/chart-utils';
import { useThemeContext } from '../../context/ThemeContext';

const WEIGHT_COLOR = '#3650e3';
const WEIGHT_BACKGROUND_COLOR = '#3650e380';
const REPETITIONS_COLOR = '#fca54e';
const REPETITIONS_BACKGROUND_COLOR = '#fca54e80';
const DURATION_COLOR = '#55a9e0';
const DURATION_BACKGROUND_COLOR = '#55a9e080';

interface ParseChartDataArgs {
  optimizedData: ExerciseChartDataPoint[];
  pointWithDataColor: string;
}
function parseOptimizedChartData({ optimizedData, pointWithDataColor }: ParseChartDataArgs): FinalChartDataPoint[] {
  const finalData: FinalChartDataPoint[] = [];

  for (const point of optimizedData) {
    const tooltip = point.inputtedByEmployee ? `Registrado por ${point.inputtedByEmployee}` : 'Registrado por el usuario';
    // Add the span start point
    finalData.push({
      x: point.x,
      y: point.y,
      tooltip,
      backgroundColor: pointWithDataColor,
    });

    // If span > 1, add intermediate points (without metadata)
    for (let i = 1; i < point.span; i++) {
      const date = new Date(point.x);
      date.setDate(date.getDate() + i);
      const intermediateDate = date.toISOString().split('T')[0]; // 'yyyy-MM-dd' format

      finalData.push({
        x: intermediateDate,
        y: point.y,
        // No metadata for intermediate points
      });
    }
  }

  return finalData;
}

interface GetExerciseProgressChartDataArgs {
  rawData: ExerciseProgressChart;
  color: string;
  pointWithDataColor: string;
}
const getExerciseProgressChartData = ({ rawData, color, pointWithDataColor }: GetExerciseProgressChartDataArgs) => {
  const dataset = rawData.datasets[0];
  return {
    datasets: [
      {
        label: dataset.label,
        data: parseOptimizedChartData({
          optimizedData: dataset.data,
          pointWithDataColor,
        }),
        // This allows setting custom colors for specific data points, with a default color for the entire dataset
        backgroundColor: (ctx: any) => ctx.raw?.backgroundColor ?? color,
        borderColor: pointWithDataColor,
        pointRadius: 0,
        fill: true,
        spanGaps: false,
        stepped: true,
        clip: false,
      },
    ],
  };
};

const getExerciseProgressChartOptions = (
  scale: ExerciseChartScale | undefined,
  { textColorSecondary, surfaceBorder }: Pick<ReturnType<typeof getColors>, 'textColorSecondary' | 'surfaceBorder'>,
  unit?: string,
): ChartOptions => ({
  animation: false,
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      filter: function (tooltipItem: any) {
        return tooltipItem.raw.y !== null && tooltipItem.raw.y !== undefined;
      },
      callbacks: {
        title: (tooltipItems: any) => {
          if (tooltipItems[0]?.label) {
            return format(tooltipItems[0]?.label, "d 'de' MMMM 'de' yyyy", { locale: es });
          }
          return '';
        },
        label: (ctx: any) => {
          const point = ctx.raw;
          const lines = [];
          lines.push(`${point.y} ${unit ?? ''}`);
          if (point.tooltip) {
            lines.push(point.tooltip);
          }
          return lines;
        },
      },
    },
  },
  interaction: {
    intersect: false, // Tooltip appears even when not directly over a point
    mode: 'index',
  },
  scales: {
    y: {
      beginAtZero: false,
      ...(scale && {
        min: scale.min,
        max: scale.max,
      }),
      ticks: {
        color: textColorSecondary,
        ...(scale && { stepSize: scale.stepSize }),
      },
      grid: {
        color: surfaceBorder,
      },
    },
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: textColorSecondary,
        callback: function (value) {
          if (typeof value === 'string') return value;
          const rawLabel = this.getLabelForValue(value);
          const date = new Date(rawLabel);
          return date.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'short',
          });
        },
      },
    },
  },
});

interface Props {
  exercise: ComputedExerciseProgress;
}

export const ExerciseProgressDetails = ({ exercise }: Props) => {
  // The axis colors are CSS variables of the active theme: subscribe to the theme so a switch renders this again and reads them.
  useThemeContext();
  const { textColorSecondary, surfaceBorder } = getColors();
  const weightChartData = useMemo(() => {
    if (!exercise.weightProgress) return undefined;
    return getExerciseProgressChartData({
      rawData: exercise.weightProgress,
      color: WEIGHT_BACKGROUND_COLOR,
      pointWithDataColor: WEIGHT_COLOR,
    });
  }, [exercise.weightProgress]);
  const weightChartOptions = useMemo(() => {
    if (!exercise.weightProgress) return undefined;
    return getExerciseProgressChartOptions(exercise.weightProgress.scale, { textColorSecondary, surfaceBorder }, 'kg');
  }, [exercise.weightProgress, textColorSecondary, surfaceBorder]);
  const repetitionsChartData = useMemo(() => {
    if (!exercise.repetitionsProgress) return undefined;
    return getExerciseProgressChartData({
      rawData: exercise.repetitionsProgress,
      color: REPETITIONS_BACKGROUND_COLOR,
      pointWithDataColor: REPETITIONS_COLOR,
    });
  }, [exercise.repetitionsProgress]);
  const repetitionsChartOptions = useMemo(() => {
    if (!exercise.repetitionsProgress) return undefined;
    return getExerciseProgressChartOptions(exercise.repetitionsProgress.scale, { textColorSecondary, surfaceBorder });
  }, [exercise.repetitionsProgress, textColorSecondary, surfaceBorder]);
  const durationChartData = useMemo(() => {
    if (!exercise.durationProgress) return undefined;
    return getExerciseProgressChartData({
      rawData: exercise.durationProgress,
      color: DURATION_BACKGROUND_COLOR,
      pointWithDataColor: DURATION_COLOR,
    });
  }, [exercise.durationProgress]);
  const durationChartOptions = useMemo(() => {
    if (!exercise.durationProgress) return undefined;
    return getExerciseProgressChartOptions(exercise.durationProgress.scale, { textColorSecondary, surfaceBorder }, 's');
  }, [exercise.durationProgress, textColorSecondary, surfaceBorder]);

  return (
    <div className='exercise-details'>
      <div>
        <div className='exercise-metric-container'>
          <h4>Peso</h4>
          <div className='exercise-metrics'>
            <ExerciseProgressMetric title='Inicial' value={exercise.initialWeight} unit='kg' />
            <ExerciseProgressMetric title='Actual' value={exercise.currentWeight} unit='kg' />
            <ExerciseProgressMetric
              title='Diferencia'
              value={exercise.weightDiff}
              diff={exercise.weightDiffPercentage}
              unit='kg'
              isBigDiff
            />
          </div>
        </div>
        {exercise.weightProgress && <Chart type='line' data={weightChartData} options={weightChartOptions} />}
      </div>

      {exercise.exerciseType === EXERCISE_TYPE.REPS && (
        <div>
          <div className='exercise-metric-container'>
            <h4>Repeticiones</h4>
            <div className='exercise-metrics'>
              <ExerciseProgressMetric title='Inicial' value={exercise.initialRepetitions} />
              <ExerciseProgressMetric title='Actual' value={exercise.currentRepetitions} />
              <ExerciseProgressMetric
                title='Diferencia'
                value={exercise.repetitionsDiff}
                diff={exercise.repetitionsDiffPercentage}
                isBigDiff
              />
            </div>
          </div>
          {exercise.repetitionsProgress && <Chart type='line' data={repetitionsChartData} options={repetitionsChartOptions} />}
        </div>
      )}

      {exercise.exerciseType === EXERCISE_TYPE.TIME && (
        <div>
          <div className='exercise-metric-container'>
            <h4>Tiempo</h4>
            <div className='exercise-metrics'>
              <ExerciseProgressMetric title='Inicial' value={exercise.initialDuration} unit='s' />
              <ExerciseProgressMetric title='Actual' value={exercise.currentDuration} unit='s' />
              <ExerciseProgressMetric
                title='Diferencia'
                value={exercise.durationDiff}
                diff={exercise.durationDiffPercentage}
                unit='s'
                isBigDiff
              />
            </div>
          </div>
          {exercise.durationProgress && <Chart type='line' data={durationChartData} options={durationChartOptions} />}
        </div>
      )}

      <div>
        <div className='exercise-metric-container'>
          <h4>RIR</h4>
          <div className='exercise-metrics'>
            <ExerciseProgressMetric title='Inicial' value={exercise.initialRir} />
            <ExerciseProgressMetric title='Actual' value={exercise.currentRir} />
            <ExerciseProgressMetric title='Diferencia' value={exercise.rirDiff} isBigDiff />
          </div>
        </div>
      </div>

      <div>
        <div className='exercise-metric-container'>
          <h4>RPE</h4>
          <div className='exercise-metrics'>
            <ExerciseProgressMetric title='Inicial' value={exercise.initialRpe} />
            <ExerciseProgressMetric title='Actual' value={exercise.currentRpe} />
            <ExerciseProgressMetric title='Diferencia' value={exercise.rpeDiff} isBigDiff />
          </div>
        </div>
      </div>
    </div>
  );
};
